// Feature flags for UI that isn't backed by a real `@scribe/core` client/hook yet — the
// context-panel views still backed by placeholder data (Insights; see its file header —
// Summary and Action items are real data and never gated; Assistant is real and has its own
// flag, below). Insights has no backend contract yet, so its content is local
// placeholder data. To keep that from ever rendering as if it were live:
//   - `panelsEnabled()` defaults OFF. When off, each panel renders a collapsed
//     "Coming soon" state with no fabricated content at all.
//   - When on, panels render their placeholder data, but always behind a clear
//     "Coming soon — preview data, not live" label (see panel/ComingSoon.tsx).
//   - Same guardrail as isMock()/isLocalAuth(): only active in dev builds
//     (import.meta.env.DEV) — a production `vite build` compiles this to a constant
//     `false`, so the flag cannot be flipped on against a real deployment.
export function panelsEnabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_FEATURE_PANELS === '1';
}

// The Assistant and in-app bug reports are NOT build flags any more: FEATURES-1 gates them
// on the server's runtime switches, `useFeatureEnabled('assistant' | 'bug_reports')`
// (lib/features.ts), so an admin can turn them on or off without a rebuild.

// ── ERRORS-1 (docs/specs/errors-1.md) ────────────────────────────────────────────────
// Unlike `panelsEnabled`, this is NOT dev-only: it gates a real, backend-backed feature and
// must be switchable in a deployed build (set at build time). Defaults OFF.

/** Offer "Retry transcription" on a recording that has been in progress impossibly long
 * (`@scribe/core` STUCK_THRESHOLDS). Pair with the backend's `SCRIBE_STALE_RETRY=1`, which is
 * what lets that retry past the 409 in-flight guard. */
export function staleRetryEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_STALE_RETRY === '1';
}

// LIVE-SAVE-1 (docs/specs/live-savings-1.md §4): auto-switch a live session to record-only
// while nobody is watching its captions, and back when they are. Unlike `panelsEnabled()`
// this is a real, deployable feature, so it is NOT dev-gated — it defaults OFF and is turned
// on per build with VITE_LIVE_AUTO_SWITCH=1.
export function liveAutoSwitchEnabled(): boolean {
  return import.meta.env.VITE_LIVE_AUTO_SWITCH === '1';
}
